import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FEELINGS, type FeelingId } from '../../constants/bridge';
import Glyph from '../glyphs/Glyph';

interface Props {
  missionTitle: string;
  therapistName: string;
  onPick: (feeling: FeelingId) => void;
  onSkip: () => void;
}

/**
 * Shown right after a mission is marked done: one tap of inner weather that
 * travels back to the therapist. No free text on purpose (council 0001).
 */
export default function HowWasItSheet({ missionTitle, therapistName, onPick, onSkip }: Props) {
  const firstBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstBtn.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onSkip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSkip]);

  return (
    <div
      className="fixed inset-0 z-[90] bg-foreground/30 flex items-end justify-center p-3 dir-rtl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="how-was-it-title"
      onClick={onSkip}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="w-full max-w-[460px] bg-card rounded-3xl border border-border p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-border" aria-hidden="true" />
        <p className="text-xs font-bold text-primary mb-1">{missionTitle}</p>
        <h2 id="how-was-it-title" className="text-lg font-extrabold text-foreground mb-4">
          איך היה?
        </h2>

        <div className="grid grid-cols-5 gap-2">
          {FEELINGS.map((f, i) => (
            <button
              key={f.id}
              ref={i === 0 ? firstBtn : undefined}
              onClick={() => onPick(f.id)}
              className="flex flex-col items-center gap-1.5 rounded-2xl bg-muted hover:bg-bridge-soft py-3 transition-colors"
            >
              <Glyph name={f.glyph} size={34} />
              <span className="text-xs font-bold text-foreground">{f.label}</span>
            </button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground leading-relaxed mt-4">
          זה יגיע ל{therapistName} לפני הפגישה, לא מיד. במצב חירום אפשר לפנות לער״ן בטלפון{' '}
          <a href="tel:1201" className="font-bold text-foreground underline underline-offset-2">1201</a>.
        </p>

        <button
          onClick={onSkip}
          className="w-full mt-3 py-2.5 text-sm font-bold text-muted-foreground hover:text-foreground rounded-xl"
        >
          דילוג
        </button>
      </motion.div>
    </div>
  );
}
